import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './DistDiscountsConfig.css';

export default function DistDiscountsConfig() {
  const navigate = useNavigate();

  const [defaultDisc,   setDefaultDisc]   = useState('');
  const [accounts,      setAccounts]      = useState([]);   // [{tag,label,discount,sku_overrides}]
  const [distCustomers, setDistCustomers] = useState([]);   // for the "tags seen" hint
  const [expanded,      setExpanded]      = useState(null); // tag currently showing sku overrides
  const [loading,       setLoading]       = useState(true);
  const [saving,        setSaving]        = useState(false);
  const [error,         setError]         = useState(null);

  const [newTag,      setNewTag]      = useState('');
  const [newLabel,    setNewLabel]    = useState('');
  const [newDiscount, setNewDiscount] = useState('');

  const [skuInputs, setSkuInputs] = useState({}); // tag -> {sku, discount}

  function reload() {
    setLoading(true);
    Promise.all([
      axios.get('/api/dist-orders/config/discounts'),
      axios.get('/api/dist-orders/dist-customers'),
    ]).then(([cfgRes, custRes]) => {
      setDefaultDisc(cfgRes.data.default_discount || '');
      setAccounts(cfgRes.data.accounts || []);
      setDistCustomers(custRes.data || []);
      setLoading(false);
    }).catch(err => {
      const status = err.response?.status;
      const detail = err.response?.data?.error;
      setError(
        status === 401
          ? 'Failed to load discount config — you may have been signed out. Try refreshing the page.'
          : `Failed to load discount config${status ? ` (${status})` : ''}${detail ? `: ${detail}` : ''}`
      );
      setLoading(false);
    });
  }

  useEffect(() => { reload(); }, []);

  // Distinct secondary tags (anything besides DIST) seen on live distributors,
  // with an example account name — helps pick the right tag when adding an
  // account, since it isn't always the obvious name (e.g. Microcenter -> MEI).
  const seenTags = {};
  distCustomers.forEach(c => {
    (c.tags || []).forEach(t => {
      if (t.toUpperCase() !== 'DIST' && !seenTags[t]) seenTags[t] = c.display;
    });
  });

  function saveDefault() {
    if (!defaultDisc) return;
    setSaving(true);
    axios.put('/api/dist-orders/config/discounts/default', { discount: defaultDisc })
      .then(r => { setAccounts(r.data.accounts || []); setSaving(false); })
      .catch(() => { setError('Failed to save default discount'); setSaving(false); });
  }

  function upsertAccount(e) {
    e.preventDefault();
    if (!newTag.trim() || !newDiscount) return;
    setSaving(true);
    axios.post('/api/dist-orders/config/discounts/accounts', {
      tag: newTag.trim(), label: newLabel.trim(), discount: newDiscount,
    }).then(r => {
      setAccounts(r.data.accounts || []);
      setNewTag(''); setNewLabel(''); setNewDiscount('');
      setSaving(false);
    }).catch(() => { setError('Failed to save account'); setSaving(false); });
  }

  function editAccountDiscount(acct, discount) {
    setSaving(true);
    axios.post('/api/dist-orders/config/discounts/accounts', {
      tag: acct.tag, label: acct.label, discount,
    }).then(r => { setAccounts(r.data.accounts || []); setSaving(false); })
      .catch(() => { setError('Failed to update discount'); setSaving(false); });
  }

  function removeAccount(tag) {
    setSaving(true);
    axios.delete(`/api/dist-orders/config/discounts/accounts/${encodeURIComponent(tag)}`)
      .then(r => { setAccounts(r.data.accounts || []); setSaving(false); })
      .catch(() => { setError('Failed to remove account'); setSaving(false); });
  }

  function addSkuOverride(tag) {
    const input = skuInputs[tag] || {};
    if (!input.sku || !input.discount) return;
    setSaving(true);
    axios.post(`/api/dist-orders/config/discounts/accounts/${encodeURIComponent(tag)}/skus`, {
      sku: input.sku, discount: input.discount,
    }).then(r => {
      setAccounts(r.data.accounts || []);
      setSkuInputs(prev => ({ ...prev, [tag]: { sku: '', discount: '' } }));
      setSaving(false);
    }).catch(() => { setError('Failed to add SKU override'); setSaving(false); });
  }

  function removeSkuOverride(tag, sku) {
    setSaving(true);
    axios.delete(`/api/dist-orders/config/discounts/accounts/${encodeURIComponent(tag)}/skus/${encodeURIComponent(sku)}`)
      .then(r => { setAccounts(r.data.accounts || []); setSaving(false); })
      .catch(() => { setError('Failed to remove SKU override'); setSaving(false); });
  }

  return (
    <div className="ddcfg-page">
      <div className="ddcfg-header">
        <button className="ddcfg-back" onClick={() => navigate('/draft-orders')}>
          ← Back
        </button>
        <div>
          <h1>Distributor Discounts</h1>
          <p className="ddcfg-muted">
            Set a custom discount rate per distributor (matched by their Shopify tag), with optional
            per-SKU overrides. Anyone can update this here going forward — no code changes needed.
          </p>
        </div>
      </div>

      {error && (
        <div className="ddcfg-error">
          {error} <button onClick={() => setError(null)}>✕</button>
        </div>
      )}

      {loading ? (
        <p className="ddcfg-muted">Loading…</p>
      ) : (
        <>
          {/* Default discount */}
          <div className="ddcfg-section">
            <label className="ddcfg-label">Default discount</label>
            <p className="ddcfg-muted-sm">Applied to any DIST-tagged distributor without a custom rate below.</p>
            <div className="ddcfg-row">
              <div className="ddcfg-pct-wrap">
                <input
                  className="ddcfg-input ddcfg-input-pct"
                  type="number" step="0.01" min="0" max="100"
                  value={defaultDisc}
                  onChange={e => setDefaultDisc(e.target.value)}
                  disabled={saving}
                />
                <span className="ddcfg-pct-sign">%</span>
              </div>
              <button className="ddcfg-btn-primary" onClick={saveDefault} disabled={saving || !defaultDisc}>
                Save
              </button>
            </div>
          </div>

          {/* Add account */}
          <div className="ddcfg-section">
            <label className="ddcfg-label">Add / update a distributor rate</label>
            <form className="ddcfg-add-form" onSubmit={upsertAccount}>
              <input
                className="ddcfg-input"
                placeholder="Shopify tag (e.g. GTS, MEI)"
                value={newTag}
                onChange={e => setNewTag(e.target.value)}
                disabled={saving}
              />
              <input
                className="ddcfg-input"
                placeholder="Label (e.g. Microcenter)"
                value={newLabel}
                onChange={e => setNewLabel(e.target.value)}
                disabled={saving}
              />
              <div className="ddcfg-pct-wrap">
                <input
                  className="ddcfg-input ddcfg-input-pct"
                  type="number" step="0.01" min="0" max="100"
                  placeholder="%"
                  value={newDiscount}
                  onChange={e => setNewDiscount(e.target.value)}
                  disabled={saving}
                />
                <span className="ddcfg-pct-sign">%</span>
              </div>
              <button className="ddcfg-btn-primary" type="submit" disabled={saving || !newTag.trim() || !newDiscount}>
                Save
              </button>
            </form>
            {Object.keys(seenTags).length > 0 && (
              <p className="ddcfg-muted-sm">
                Tags currently seen on distributors: {Object.entries(seenTags).map(([t, name]) => (
                  <span key={t} className="ddcfg-tag-hint">{t} <em>({name})</em></span>
                ))}
              </p>
            )}
          </div>

          {/* Accounts table */}
          {accounts.length === 0 ? (
            <p className="ddcfg-muted">No custom distributor rates configured yet — everyone gets the default.</p>
          ) : (
            <div className="ddcfg-table-wrap">
              <table className="ddcfg-table">
                <thead>
                  <tr>
                    <th>Label</th>
                    <th>Tag</th>
                    <th>Discount</th>
                    <th>SKU overrides</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {accounts.map(acct => (
                    <React.Fragment key={acct.tag}>
                      <tr>
                        <td className="ddcfg-account-label">{acct.label}</td>
                        <td className="ddcfg-account-tag">{acct.tag}</td>
                        <td>
                          <div className="ddcfg-pct-wrap ddcfg-pct-wrap-sm">
                            <input
                              className="ddcfg-input ddcfg-input-pct-sm"
                              type="number" step="0.01" min="0" max="100"
                              defaultValue={acct.discount}
                              disabled={saving}
                              onBlur={e => {
                                if (e.target.value && e.target.value !== acct.discount) {
                                  editAccountDiscount(acct, e.target.value);
                                }
                              }}
                            />
                            <span className="ddcfg-pct-sign">%</span>
                          </div>
                        </td>
                        <td>
                          <button
                            className="ddcfg-link-btn"
                            onClick={() => setExpanded(expanded === acct.tag ? null : acct.tag)}
                          >
                            {Object.keys(acct.sku_overrides || {}).length} override
                            {Object.keys(acct.sku_overrides || {}).length === 1 ? '' : 's'}
                            {expanded === acct.tag ? ' ▲' : ' ▼'}
                          </button>
                        </td>
                        <td>
                          <button className="ddcfg-remove" disabled={saving} onClick={() => removeAccount(acct.tag)}>
                            Remove
                          </button>
                        </td>
                      </tr>
                      {expanded === acct.tag && (
                        <tr className="ddcfg-expand-row">
                          <td colSpan={5}>
                            <div className="ddcfg-sku-panel">
                              {Object.entries(acct.sku_overrides || {}).length === 0 ? (
                                <p className="ddcfg-muted-sm">No SKU-specific overrides for {acct.label} yet.</p>
                              ) : (
                                <ul className="ddcfg-sku-list">
                                  {Object.entries(acct.sku_overrides || {}).map(([sku, disc]) => (
                                    <li key={sku}>
                                      <span className="ddcfg-sku-name">{sku}</span>
                                      <span className="ddcfg-sku-disc">{disc}%</span>
                                      <button
                                        className="ddcfg-remove"
                                        disabled={saving}
                                        onClick={() => removeSkuOverride(acct.tag, sku)}
                                      >
                                        Remove
                                      </button>
                                    </li>
                                  ))}
                                </ul>
                              )}
                              <div className="ddcfg-sku-add">
                                <input
                                  className="ddcfg-input"
                                  placeholder="SKU"
                                  value={(skuInputs[acct.tag] || {}).sku || ''}
                                  onChange={e => setSkuInputs(prev => ({
                                    ...prev, [acct.tag]: { ...(prev[acct.tag] || {}), sku: e.target.value },
                                  }))}
                                  disabled={saving}
                                />
                                <div className="ddcfg-pct-wrap ddcfg-pct-wrap-sm">
                                  <input
                                    className="ddcfg-input ddcfg-input-pct-sm"
                                    type="number" step="0.01" min="0" max="100"
                                    placeholder="%"
                                    value={(skuInputs[acct.tag] || {}).discount || ''}
                                    onChange={e => setSkuInputs(prev => ({
                                      ...prev, [acct.tag]: { ...(prev[acct.tag] || {}), discount: e.target.value },
                                    }))}
                                    disabled={saving}
                                  />
                                  <span className="ddcfg-pct-sign">%</span>
                                </div>
                                <button
                                  className="ddcfg-btn-primary"
                                  disabled={saving}
                                  onClick={() => addSkuOverride(acct.tag)}
                                >
                                  Add override
                                </button>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
